import { purchaserName } from '../lib/purchaser';
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import walletIcon from "@iconify/icons-solar/wallet-money-linear";
import searchIcon from "@iconify/icons-solar/magnifer-linear";
import refreshIcon from "@iconify/icons-solar/refresh-linear";
import arrowIcon from "@iconify/icons-solar/arrow-right-linear";
import TopBar from "../components/layout/TopBar";
import SalesDialog from "../components/sales/SalesDialog";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import { useConfirmation } from "../context/ConfirmationContext";
import { api } from "../lib/api";
import "../components/sales/PipelineBoard.css";
import "./Payments.css";

type View = "schedules" | "overdue" | "ledger" | "tools";
interface Schedule {
  id: string;
  saleId: string;
  amount: number;
  amountPaid: number;
  penaltyAmount: number;
  outstanding: number;
  dueDate: string;
  status: string;
  daysOverdue: number;
  installmentNo: number;
  paymentMethod?: string;
  receiptNumber?: string;
  notes?: string;
  sale: Sale;
  ledgerEntries?: Entry[];
}
interface Sale {
  otpDocument?: { issue: { draftId: string } };
  paymentIssues?: string[];
  id: string;
  clientId: string;
  updatedAt: string;
  stage: string;
  saleAmount: number;
  downPayment: number;
  balance: number;
  totalPaid: number;
  payments: Schedule[];
  client: {
    id: string;
    firstName: string;
    lastName: string;
    phone?: string;
    email?: string;
  };
  lot: { lotNumber: string };
}
interface OtpAccount { draftId: string; purchaser: string; lots: string[]; }
interface Entry {
  id: string;
  type: string;
  amount: number;
  allocatedAmount?: number;
  runningBalance: number;
  description: string;
  referenceNumber?: string;
  createdAt: string;
  client?: Sale["client"];
  sale?: { id: string; lot: { lotNumber: string } };
  metadata?: { paymentMethod?: string; receiptNumber?: string; notes?: string };
}
interface Stats {
  reconciliationIssues: {
    saleId: string;
    lotNumber: string;
    issues: string[];
  }[];
  outstandingAmount: number;
  overdueAmount: number;
  overdueCount: number;
  pendingCount: number;
  upcomingDue7Days: number;
  upcomingAmount: number;
  monthlyCollected: number;
  totalCollected: number;
  totalScheduledAmount: number;
  collectionEfficiency: number;
  totalPenalties: number;
  asOf: string;
}
const fmt = (n: number) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(n);
const date = (d: string) =>
  new Date(d).toLocaleDateString("en-PH", {
    timeZone: "Asia/Manila",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
const name = (c?: Sale["client"]) =>
  c ? purchaserName(c) : "Client unavailable";
const label = (s: string) =>
  s
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());
const owed = (p: Schedule) =>
  Math.max(
    0,
    Math.round(p.amount * 100) +
      Math.round(p.penaltyAmount * 100) -
      Math.round(p.amountPaid * 100),
  ) / 100;
const available = (sale?: Sale | null) =>
  sale?.payments
    .filter((p) => p.status !== "CANCELLED")
    .reduce((n, p) => n + Math.round(owed(p) * 100), 0)! / 100 || 0;
const requestId = () => crypto.randomUUID();
const methods = ["CASH", "BANK_TRANSFER", "GCASH", "CHECK", "CARD"];
const emptyForm = () => ({
  amount: "",
  paymentMethod: "CASH",
  receiptNumber: "",
  referenceNumber: "",
  notes: "",
  requestId: requestId(),
});

export default function Payments() {
  const { addToast } = useToast(),
    { user } = useAuth(),
    { confirmAction } = useConfirmation(),
    navigate = useNavigate();
  const admin = user?.role === "ADMIN";
  const [view, setView] = useState<View>(
    new URLSearchParams(window.location.search).get("view") === "overdue"
      ? "overdue"
      : "schedules",
  );
  const [stats, setStats] = useState<Stats | null>(null),
    [aging, setAging] = useState<any>(null),
    [rows, setRows] = useState<Schedule[]>([]),
    [entries, setEntries] = useState<Entry[]>([]);
  const [query, setQuery] = useState(""),
    [search, setSearch] = useState(""),
    [status, setStatus] = useState(""),
    [page, setPage] = useState(1),
    [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true),
    [loadError, setLoadError] = useState(""),
    [refresh, setRefresh] = useState(0);
  const [dialog, setDialog] = useState<
    "record" | "detail" | "penalties" | "adjustment" | "schedule" | null
  >(null);
  const [sales, setSales] = useState<Sale[]>([]),
    [selected, setSelected] = useState<Sale | null>(null),
    [saleQuery, setSaleQuery] = useState("");
  const [accountLoadFailed, setAccountLoadFailed] = useState(false);
  const [otpAccounts, setOtpAccounts] = useState<OtpAccount[]>([]);
  const [detail, setDetail] = useState<Schedule | null>(null),
    [preview, setPreview] = useState<any>(null);
  const [form, setForm] = useState(emptyForm),
    [adjust, setAdjust] = useState({
      amount: "",
      description: "",
      referenceNumber: "",
      requestId: requestId(),
    }),
    [schedule, setSchedule] = useState({
      frequency: "monthly",
      startDate: new Date().toLocaleDateString("en-CA", {
        timeZone: "Asia/Manila",
      }),
    });
  const [busy, setBusy] = useState(false),
    [formLoading, setFormLoading] = useState(false),
    [formError, setFormError] = useState("");
  const busyRef = useRef(false),
    dialogRequest = useRef(0);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    const params: Record<string, string> = {
      page: String(page),
      limit: "25",
      search,
    };
    if (view === "overdue") params.overdue = "true";
    else if (status) params.status = status;
    Promise.all([
      api.payments.stats(),
      api.payments.overdue.summary(),
      view === "ledger"
        ? api.payments.ledger.list(params)
        : view === "tools"
          ? Promise.resolve({ total: 0 })
          : api.payments.schedules.list(params),
    ])
      .then(([s, a, data]) => {
        if (cancelled) return;
        setStats(s);
        setAging(a);
        setTotal(data.total);
        if(data.total>0 && page>Math.ceil(data.total/25))setPage(Math.ceil(data.total/25));
        setRows(data.payments || []);
        setEntries(data.entries || []);
      })
      .catch((e) => {
        if (!cancelled)
          setLoadError(
            e.message || "Payments could not be loaded. Please retry.",
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view, page, status, search, refresh]);
  const switchView = (v: View) => {
    setView(v);
    setPage(1);
    setQuery("");
    setSearch("");
    setStatus("");
  };
  const close = () => {
    if (busyRef.current) return;
    dialogRequest.current++;
    setDialog(null);
    setFormError("");
  };
  const open = async (
    kind: "record" | "adjustment" | "schedule",
    saleId?: string,
    amount?: number,
  ) => {
    const revision = ++dialogRequest.current;
    setDialog(kind);
    setSelected(null);
    setSales([]);
    setOtpAccounts([]);
    setAccountLoadFailed(false);
    setSaleQuery("");
    setForm({ ...emptyForm(), amount: amount ? String(amount) : "" });
    setAdjust({
      amount: "",
      description: "",
      referenceNumber: "",
      requestId: requestId(),
    });
    setFormError("");
    setFormLoading(true);
    try {
      const data = await api.payments.accounts();
      if (revision !== dialogRequest.current) return;
      const eligible: Sale[] = data.sales.filter(
        (s: Sale) => !["CANCELLED", "COMPLETED"].includes(s.stage),
      );
      setSales(eligible);
      setOtpAccounts(data.otpAccounts || []);
      if (saleId) setSelected(eligible.find((s) => s.id === saleId) || null);
    } catch (e: any) {
      if (revision === dialogRequest.current) { setFormError(e.message); setAccountLoadFailed(true); }
    } finally {
      if (revision === dialogRequest.current) setFormLoading(false);
    }
  };
  const showDetail = async (p: Schedule) => {
    const revision = ++dialogRequest.current;
    setDialog("detail");
    setDetail(null);
    setFormLoading(true);
    setFormError("");
    try {
      const data = await api.payments.schedules.get(p.id);
      if (revision === dialogRequest.current) setDetail(data);
    } catch (e: any) {
      if (revision === dialogRequest.current) setFormError(e.message);
    } finally {
      if (revision === dialogRequest.current) setFormLoading(false);
    }
  };
  const save = async (action: () => Promise<any>, title: string) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setFormError("");
    try {
      await action();
      setDialog(null);
      addToast("success", title);
      setRefresh((n) => n + 1);
    } catch (e: any) {
      setFormError(e.message);
      addToast("error", "Changes not saved", e.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  const record = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected || selected.paymentIssues?.length) return;
    const amount = Number(form.amount);
    if (
      !Number.isFinite(amount) ||
      amount <= 0 ||
      amount > available(selected)
    ) {
      setFormError("Enter an amount within the outstanding scheduled balance.");
      return;
    }
    save(
      () =>
        api.payments.record({
          ...form,
          saleId: selected.id,
          amount,
          expectedUpdatedAt: selected.updatedAt,
        }),
      "Payment recorded",
    );
  };
  const previewPenalties = async () => {
    const revision = ++dialogRequest.current;
    setDialog("penalties"); setPreview(null); setFormError(""); setFormLoading(true);
    try {
      const result = await api.payments.penalties.compute({preview:true});
      if(revision===dialogRequest.current)setPreview(result);
    } catch(e:any) {
      if(revision===dialogRequest.current)setFormError(e.message);
    } finally {
      if(revision===dialogRequest.current)setFormLoading(false);
    }
  };
  const applyPenalties = () => {
    if (!preview) return;
    confirmAction({
      title: "Apply these penalties?",
      description: `Add ${fmt(preview.totalAmount)} across ${preview.penalized} overdue installments. This adds charges to the clients’ balances.`,
      confirmLabel: "Apply penalties",
      pendingLabel: "Applying…",
      successTitle: "Penalties applied",
      action: async () => {
        await api.payments.penalties.compute({
          preview: false,
          fingerprint: preview.fingerprint,
        });
        setDialog(null);
        setRefresh((n) => n + 1);
      },
    });
  };
  const selectSale = (id: string) => {
    setSelected(sales.find((s) => s.id === id) || null);
    setForm((f) => ({ ...f, amount: "", requestId: requestId() }));
    setFormError("");
  };
  const terms = saleQuery.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  const matches = (text: string) => terms.every(term => text.toLocaleLowerCase().includes(term));
  const matchingSales = sales.filter(s => matches(`${name(s.client)} ${s.lot.lotNumber}`));
  const matchingOtp = dialog === "record" ? otpAccounts.filter(a => matches(`${a.purchaser} ${a.lots.join(" ")}`)) : [];
  const matchCount = matchingSales.length + matchingOtp.length;
  const salePicker = (
    <div className="payment-sale-picker">
      <label>
        Find client or lot
        <input
          type="search"
          value={saleQuery}
          onChange={(e) => setSaleQuery(e.target.value)}
          placeholder="Search by client name or lot"
          disabled={busy || formLoading}
          aria-describedby="payment-search-status"
        />
      </label>
      <p id="payment-search-status" className="payment-search-status" role="status">
        {formLoading ? "Loading accounts…" : accountLoadFailed ? "Accounts could not be loaded. Retry to search." : terms.length ? `${matchCount} matching ${matchCount === 1 ? "account" : "accounts"}` : "Search for a client or lot, or select a sale below."}
      </p>
      {!formLoading && !accountLoadFailed && terms.length > 0 && (
        <div className="payment-search-results" aria-label="Matching accounts">
          {matchingSales.map(s => (
            <button type="button" key={s.id} disabled={busy} aria-pressed={selected?.id === s.id}
              onClick={() => selectSale(s.id)} className="payment-search-result">
              <span><strong>{name(s.client)}</strong><small>Lot {s.lot.lotNumber} · Sale</small></span>
              <span>{selected?.id === s.id ? "Selected" : "Select sale"}</span>
            </button>
          ))}
          {matchingOtp.map(a => (
            <button type="button" key={a.draftId} disabled={busy} className="payment-search-result"
              onClick={() => navigate(`/sales/otp/${a.draftId}/receipts`)}>
              <span><strong>{a.purchaser}</strong><small>{a.lots.join(", ")} · OTP account</small></span>
              <span>Open OTP receipts <Icon icon={arrowIcon} aria-hidden="true" /></span>
            </button>
          ))}
          {!matchingSales.length && !matchingOtp.length && <p>No matching accounts. Try another client name or lot number.</p>}
        </div>
      )}
      <label>
        Sale
        <select
          required
          value={selected?.id || ""}
          disabled={busy || formLoading}
          onChange={(e) => selectSale(e.target.value)}
        >
          <option value="">Select a sale</option>
          {sales
            .filter(s => s.id === selected?.id || matches(`${name(s.client)} ${s.lot.lotNumber}`))
            .map((s) => (
              <option key={s.id} value={s.id}>
                {name(s.client)} · Lot {s.lot.lotNumber}
              </option>
            ))}
        </select>
      </label>
      {selected && (
        <div className="payment-sale-summary">
          <span>
            {name(selected.client)} · Lot {selected.lot.lotNumber}
          </span>
          <strong>{fmt(available(selected))} scheduled balance</strong>
          <small>Includes unpaid installments and applied penalties.</small>
          {!!selected.paymentIssues?.length && (
            <p className="payment-danger">
              Reconciliation required: {selected.paymentIssues.join(" ")}
            </p>
          )}
        </div>
      )}
      {!formLoading && !accountLoadFailed && !sales.length && <p>{dialog === "record" && otpAccounts.length ? "No regular sales are available. Search above for OTP accounts." : "No open sales are available."}</p>}
    </div>
  );
  const pageCount = Math.ceil(total / 25);
  const table = (
    <div
      className="payment-table-wrap"
      tabIndex={0}
      role="region"
      aria-label={
        view === "ledger"
          ? "Ledger entries, scroll horizontally for more columns"
          : "Payment schedules, scroll horizontally for more columns"
      }
    >
      <table className="payment-table">
        <caption className="pipeline-sr">
          {view === "ledger"
            ? "Recorded journal entries"
            : "Payment installments"}
        </caption>
        <thead>
          <tr>
            {(view === "ledger"
              ? ["Recorded", "Client / lot", "Entry", "Reference", "Amount", ""]
              : [
                  "Client / lot",
                  "Due date",
                  "Scheduled",
                  "Received",
                  "Outstanding",
                  "Status",
                  "",
                ]
            ).map((h, i) => (
              <th
                key={i}
                scope="col"
                className={
                  view === "ledger"
                    ? i === 4
                      ? "numeric"
                      : ""
                    : i >= 2 && i <= 4
                      ? "numeric"
                      : ""
                }
              >
                {h || <span className="pipeline-sr">Actions</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {view === "ledger"
            ? entries.map((e) => (
                <tr key={e.id}>
                  <td>
                    {date(e.createdAt)}
                    <small>
                      {new Date(e.createdAt).toLocaleTimeString("en-PH", {
                        timeZone: "Asia/Manila",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </small>
                  </td>
                  <td>
                    <strong>{name(e.client)}</strong>
                    <small>
                      {e.sale
                        ? `Lot ${e.sale.lot.lotNumber}`
                        : "Client journal"}
                    </small>
                  </td>
                  <td>
                    <strong>{label(e.type)}</strong>
                    <small className="payment-description">
                      {e.description}
                    </small>
                  </td>
                  <td>
                    {e.referenceNumber || "Not recorded"}
                    <small>
                      {e.metadata?.paymentMethod
                        ? label(e.metadata.paymentMethod)
                        : ""}
                    </small>
                  </td>
                  <td className="numeric">
                    <strong>
                      {e.amount > 0 ? "+" : ""}
                      {fmt(e.amount)}
                    </strong>
                    <small>{e.amount >= 0 ? "Credit" : "Debit"}</small>
                  </td>
                  <td>
                    {e.sale && (
                      <button
                        className="payment-link"
                        onClick={() =>
                          navigate("/sales", { state: { saleId: e.sale!.id } })
                        }
                      >
                        View sale
                        <Icon icon={arrowIcon} />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            : rows.map((p) => (
                <tr key={p.id}>
                  <td>
                    <button
                      className="payment-client"
                      onClick={() => showDetail(p)}
                    >
                      {name(p.sale.client)}
                    </button>
                    <small>
                      Lot {p.sale.lot.lotNumber}
                      {p.installmentNo > 0
                        ? ` · Installment ${p.installmentNo}`
                        : ""}
                    </small>
                  </td>
                  <td>
                    {date(p.dueDate)}
                    {p.daysOverdue > 0 && p.outstanding > 0 && (
                      <small className="payment-danger">
                        {p.daysOverdue} days overdue
                      </small>
                    )}
                  </td>
                  <td className="numeric">
                    {fmt(p.amount)}
                    {p.penaltyAmount > 0 && (
                      <small>+ {fmt(p.penaltyAmount)} penalty</small>
                    )}
                  </td>
                  <td className="numeric">{fmt(p.amountPaid)}</td>
                  <td className="numeric">
                    <strong>{fmt(p.outstanding)}</strong>
                  </td>
                  <td>
                    <span
                      className={`payment-status ${p.status.toLowerCase()}`}
                    >
                      {label(p.status)}
                    </span>
                    {p.status === "OVERDUE" && p.amountPaid > 0 && (
                      <small>Partially paid</small>
                    )}
                  </td>
                  <td>
                    <div className="payment-row-actions">
                      {p.outstanding > 0 &&
                        !["COMPLETED", "CANCELLED"].includes(p.sale.stage) && (
                          <button
                            className="payment-link"
                            onClick={() =>
                              p.sale.otpDocument ? navigate(`/sales/otp/${p.sale.otpDocument.issue.draftId}/receipts`) : open("record", p.saleId, p.outstanding)
                            }
                          >
                            {p.sale.otpDocument ? "Receipts and clearance" : "Record payment"}
                          </button>
                        )}
                      <button
                        className="payment-link"
                        aria-label={`View installment for ${name(p.sale.client)}, due ${date(p.dueDate)}`}
                        onClick={() => showDetail(p)}
                      >
                        Details
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
  return (
    <>
      <TopBar title="Payments & Ledger" showSearch={false} />
      <main className="payments-workspace">
        <header className="payments-heading">
          <div>
            <h2>Payments</h2>
            <p>
              Track installments, record receipts, and review outstanding
              balances.
            </p>
          </div>
          <button
            className="payment-button primary"
            onClick={() => open("record")}
          >
            <Icon icon={walletIcon} width={18} />
            Record payment
          </button>
        </header>
        {stats && !loadError && (
          <>
            <dl className="payment-summary">
              <div>
                <dt>Scheduled balance</dt>
                <dd>{fmt(stats.outstandingAmount)}</dd>
                <small>{stats.pendingCount} unpaid installments</small>
              </div>
              <div>
                <dt>Overdue</dt>
                <dd className={stats.overdueCount ? "payment-danger" : ""}>
                  {fmt(stats.overdueAmount)}
                </dd>
                <button
                  className="payment-link"
                  onClick={() => switchView("overdue")}
                >
                  {stats.overdueCount} overdue installments{" "}
                  <Icon icon={arrowIcon} />
                </button>
              </div>
              <div>
                <dt>Due in the next 7 days</dt>
                <dd>{fmt(stats.upcomingAmount)}</dd>
                <small>
                  {stats.upcomingDue7Days} installments · includes today
                </small>
              </div>
              <div>
                <dt>Receipts this month</dt>
                <dd>{fmt(stats.monthlyCollected)}</dd>
                <small>Posted receipts · Philippine time</small>
              </div>
            </dl>
            <div className="payment-context">
              <span>
                <strong>{fmt(stats.totalCollected)}</strong> received against{" "}
                {fmt(stats.totalScheduledAmount)} scheduled
              </span>
              <span>
                {stats.collectionEfficiency}% collected, including penalties ·{" "}
                {fmt(stats.totalPenalties)} penalties applied
              </span>
            </div>
          </>
        )}
        {stats && !loadError && !!stats.reconciliationIssues?.length && (
          <details className="payment-reconciliation">
            <summary>
              {stats.reconciliationIssues.length} accounts need reconciliation
            </summary>
            <p>
              Contract totals and installment history disagree. Recording
              payments, rescheduling, and penalties are blocked for these
              accounts until reviewed. Summary amounts above reflect existing
              installment records.
            </p>
            {stats.reconciliationIssues.map((a) => (
              <div key={a.saleId}>
                <strong>Lot {a.lotNumber}</strong>
                <ul>
                  {a.issues.map((i) => (
                    <li key={i}>{i}</li>
                  ))}
                </ul>
                <button
                  className="payment-link"
                  onClick={() =>
                    navigate("/sales", { state: { saleId: a.saleId } })
                  }
                >
                  Review sale
                </button>
              </div>
            ))}
          </details>
        )}
        <div className="payment-navigation">
          <nav aria-label="Payment views">
            {(
              [
                "schedules",
                "overdue",
                "ledger",
                ...(admin ? ["tools"] : []),
              ] as View[]
            ).map((v) => (
              <button
                key={v}
                aria-current={view === v ? "page" : undefined}
                onClick={() => switchView(v)}
              >
                {v === "tools" ? "Management" : label(v)}
                {v === "overdue" && !!stats?.overdueCount && (
                  <span>{stats.overdueCount}</span>
                )}
              </button>
            ))}
          </nav>
          <button
            className="payment-link"
            disabled={loading}
            onClick={() => setRefresh((n) => n + 1)}
          >
            <Icon icon={refreshIcon} width={16} />
            Refresh
          </button>
        </div>
        {loadError ? (
          <div className="payment-empty" role="alert">
            <h3>Payments could not be loaded</h3>
            <p>{loadError}</p>
            <button
              className="payment-button"
              onClick={() => setRefresh((n) => n + 1)}
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {view !== "tools" && (
              <div className="payment-filters">
                <label className="payment-search">
                  <span>
                    Search {view === "ledger" ? "ledger" : "installments"}
                  </span>
                  <div>
                    <Icon icon={searchIcon} width={18} />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      type="search"
                      placeholder={
                        view === "ledger"
                          ? "Client, lot, receipt or description"
                          : "Client or lot number"
                      }
                    />
                  </div>
                </label>
                {view === "schedules" && (
                  <label>
                    Status
                    <select
                      value={status}
                      onChange={(e) => {
                        setStatus(e.target.value);
                        setPage(1);
                      }}
                    >
                      <option value="">All statuses</option>
                      {["PENDING", "PARTIAL", "OVERDUE", "PAID"].map((s) => (
                        <option key={s} value={s}>
                          {label(s)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <span className="payment-result-count" role="status">
                  {loading
                    ? "Loading…"
                    : `${total} ${view === "ledger" ? "entries" : "installments"}`}
                </span>
              </div>
            )}
            {view === "overdue" && aging && (
              <div
                className="payment-aging"
                aria-label="Overdue aging across all accounts"
              >
                {[
                  ["days30", "1–30 days"],
                  ["days60", "31–60 days"],
                  ["days90", "61–90 days"],
                  ["days90plus", "Over 90 days"],
                ].map(([key, title]) => (
                  <div key={key}>
                    <span>{title}</span>
                    <strong>{fmt(aging.agingAmounts[key])}</strong>
                    <small>{aging.aging[key]} installments</small>
                  </div>
                ))}
              </div>
            )}
            {view === "ledger" && (
              <p className="payment-note">
                Credits are receipts or journal credits; debits are charges or
                refunds. Journal activity is separate from the outstanding
                installment balance. Historical payments without receipt entries
                are excluded from “Receipts this month.”
              </p>
            )}
            {loading ? (
              <div
                className="payment-loading"
                role="status"
                aria-label="Loading payments"
              >
                <div />
                <div />
                <div />
                <div />
              </div>
            ) : view === "tools" ? (
              <section
                className="payment-management"
                aria-label="Payment management"
              >
                <div>
                  <h3>Review late-payment penalties</h3>
                  <p>
                    Preview a one-time 2% charge on the original installment
                    amount for balances at least one day overdue. Paid
                    installments and closed sales are excluded.
                  </p>
                  <button className="payment-button" onClick={previewPenalties}>
                    Preview penalties
                  </button>
                </div>
                <div>
                  <h3>Generate an installment schedule</h3>
                  <p>
                    Set monthly or quarterly due dates. Existing schedules can
                    only be replaced before any receipts or penalties are
                    recorded.
                  </p>
                  <button
                    className="payment-button"
                    onClick={() => open("schedule")}
                  >
                    Set up schedule
                  </button>
                </div>
                <div>
                  <h3>Correct the journal</h3>
                  <p>
                    Add a documented credit or debit to the client journal. This
                    does not change installment balances, issue a refund, or
                    record a payment.
                  </p>
                  <button
                    className="payment-button"
                    onClick={() => open("adjustment")}
                  >
                    Add journal adjustment
                  </button>
                </div>
              </section>
            ) : total === 0 ? (
              <div className="payment-empty">
                <h3>
                  {search || status
                    ? "No matching results"
                    : view === "overdue"
                      ? "No overdue installments"
                      : view === "ledger"
                        ? "No journal entries yet"
                        : "No payment schedules yet"}
                </h3>
                <p>
                  {search || status
                    ? "Try a different client, lot, or status."
                    : view === "overdue"
                      ? "All current installments are up to date."
                      : view === "ledger"
                        ? "Recorded receipts and charges will appear here."
                        : "Create a schedule for an open sale to start tracking installments."}
                </p>
                {(search || status) && (
                  <button
                    className="payment-button"
                    onClick={() => {
                      setQuery("");
                      setSearch("");
                      setStatus("");
                      setPage(1);
                    }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <>
                {table}
                <footer className="payment-pagination">
                  <span>
                    Showing {(page - 1) * 25 + 1}–{Math.min(page * 25, total)}{" "}
                    of {total}
                  </span>
                  <div>
                    <button
                      className="payment-button"
                      disabled={page <= 1}
                      onClick={() => setPage((n) => n - 1)}
                    >
                      Previous
                    </button>
                    <span>
                      Page {page} of {pageCount}
                    </span>
                    <button
                      className="payment-button"
                      disabled={page >= pageCount}
                      onClick={() => setPage((n) => n + 1)}
                    >
                      Next
                    </button>
                  </div>
                </footer>
              </>
            )}
          </>
        )}
      </main>
      <SalesDialog
        open={dialog !== null}
        title={
          dialog === "record"
            ? "Record a payment"
            : dialog === "detail"
              ? "Installment details"
              : dialog === "penalties"
                ? "Penalty preview"
                : dialog === "schedule"
                  ? "Installment schedule"
                  : "Journal adjustment"
        }
        onClose={close}
      >
        <div className="payment-form">
          {formLoading && <p role="status">Loading current account details…</p>}
          {formError && (
            <div className="payment-form-error" role="alert">
              {formError}
            </div>
          )}
          {dialog === "record" && (
            <form onSubmit={record}>
              <p>
                Record money already received. The amount is applied to the
                oldest unpaid installments first.
              </p>
              {salePicker}
              <fieldset disabled={busy || formLoading || !selected}>
                <label>
                  Amount received (₱)
                  <input
                    required
                    type="number"
                    min="0.01"
                    step="0.01"
                    max={available(selected)}
                    value={form.amount}
                    onChange={(e) =>
                      setForm({ ...form, amount: e.target.value })
                    }
                  />
                </label>
                <label>
                  Payment method
                  <select
                    value={form.paymentMethod}
                    onChange={(e) =>
                      setForm({ ...form, paymentMethod: e.target.value })
                    }
                  >
                    {methods.map((m) => (
                      <option key={m} value={m}>
                        {label(m)}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="payment-form-grid">
                  <label>
                    Receipt number
                    <input
                      maxLength={100}
                      value={form.receiptNumber}
                      onChange={(e) =>
                        setForm({ ...form, receiptNumber: e.target.value })
                      }
                      placeholder="Optional"
                    />
                  </label>
                  <label>
                    Transaction reference
                    <input
                      maxLength={100}
                      value={form.referenceNumber}
                      onChange={(e) =>
                        setForm({ ...form, referenceNumber: e.target.value })
                      }
                      placeholder="Optional"
                    />
                  </label>
                </div>
                <label>
                  Notes
                  <textarea
                    maxLength={1000}
                    value={form.notes}
                    onChange={(e) =>
                      setForm({ ...form, notes: e.target.value })
                    }
                    rows={2}
                  />
                </label>
              </fieldset>
              {selected &&
                Number(form.amount) > 0 &&
                Number(form.amount) <= available(selected) && (
                  <div className="payment-allocation">
                    <strong>Allocation preview</strong>
                    {(() => {
                      let left = Math.round(Number(form.amount) * 100);
                      return [...selected.payments]
                        .filter((p) => p.status !== "CANCELLED" && owed(p) > 0)
                        .sort(
                          (a, b) =>
                            a.dueDate.localeCompare(b.dueDate) ||
                            a.installmentNo - b.installmentNo ||
                            a.id.localeCompare(b.id),
                        )
                        .map((p) => {
                          const amount = Math.min(
                            left,
                            Math.round(owed(p) * 100),
                          );
                          left -= amount;
                          return amount > 0 ? (
                            <div key={p.id}>
                              <span>Due {date(p.dueDate)}</span>
                              <span>{fmt(amount / 100)}</span>
                            </div>
                          ) : null;
                        });
                    })()}
                  </div>
                )}
              <footer>
                <button
                  type="button"
                  className="payment-button"
                  disabled={busy}
                  onClick={close}
                >
                  Cancel
                </button>
                <button
                  className="payment-button primary"
                  disabled={
                    busy ||
                    formLoading ||
                    !selected ||
                    available(selected) <= 0 ||
                    !!selected?.paymentIssues?.length
                  }
                >
                  {busy ? "Recording…" : "Record payment"}
                </button>
              </footer>
              {formError && selected && (
                <button
                  type="button"
                  className="payment-link"
                  disabled={busy}
                  onClick={() => open("record", selected.id)}
                >
                  Reload current balance
                </button>
              )}
            </form>
          )}
          {dialog === "detail" && detail && (
            <>
              <div className="payment-detail-heading">
                <h3>{name(detail.sale.client)}</h3>
                <p>
                  Lot {detail.sale.lot.lotNumber} · Due {date(detail.dueDate)}
                </p>
                <span
                  className={`payment-status ${detail.status.toLowerCase()}`}
                >
                  {label(detail.status)}
                </span>
              </div>
              <dl className="payment-detail-numbers">
                {[
                  ["Scheduled", detail.amount],
                  ["Penalties", detail.penaltyAmount],
                  ["Received", detail.amountPaid],
                  ["Outstanding", detail.outstanding],
                ].map(([title, value]) => (
                  <div key={title}>
                    <dt>{title}</dt>
                    <dd>{fmt(Number(value))}</dd>
                  </div>
                ))}
              </dl>
              <h3>Receipt & charge history</h3>
              {detail.ledgerEntries?.length ? (
                <div className="payment-history">
                  {detail.ledgerEntries.map((e) => (
                    <div key={e.id}>
                      <div>
                        <strong>{label(e.type)}</strong>
                        <small>
                          {date(e.createdAt)} ·{" "}
                          {e.referenceNumber || "No reference"}
                        </small>
                      </div>
                      <strong>{fmt(e.allocatedAmount ?? e.amount)}</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <p>
                  No journal entries are linked to this installment. Historical
                  received amounts may predate receipt tracking.
                </p>
              )}
              {detail.notes && <p>Notes: {detail.notes}</p>}
              <footer>
                <button
                  className="payment-button"
                  onClick={() => {
                    close();
                    navigate("/sales", { state: { saleId: detail.saleId } });
                  }}
                >
                  View sale
                </button>
                {detail.outstanding > 0 &&
                  !["CANCELLED", "COMPLETED"].includes(detail.sale.stage) && (
                    <button
                      className="payment-button primary"
                      onClick={() =>
                        detail.sale.otpDocument ? navigate(`/sales/otp/${detail.sale.otpDocument.issue.draftId}/receipts`) : open("record", detail.saleId, detail.outstanding)
                      }
                    >
                      {detail.sale.otpDocument ? "Receipts and clearance" : "Record payment"}
                    </button>
                  )}
              </footer>
            </>
          )}
          {dialog === "penalties" && preview && (
            <>
              <p>
                One-time <strong>2%</strong> of the original installment amount,
                after at least one overdue day. Amounts are added to outstanding
                balances.
              </p>
              <div className="payment-preview-total">
                <strong>{fmt(preview.totalAmount)}</strong>
                <span>{preview.penalized} eligible installments</span>
              </div>
              <div className="payment-history">
                {preview.entries.map((p: any) => (
                  <div key={p.paymentId}>
                    <div>
                      <strong>
                        {p.clientName} · Lot {p.lotNumber}
                      </strong>
                      <small>
                        {p.daysOverdue} days overdue · {fmt(p.originalAmount)}{" "}
                        installment
                      </small>
                    </div>
                    <strong>{fmt(p.penaltyAmount)}</strong>
                  </div>
                ))}
              </div>
              {!!preview.excludedAccounts && (
                <p className="payment-danger">
                  {preview.excludedAccounts} overdue accounts excluded because
                  their totals need reconciliation.
                </p>
              )}
              {!preview.penalized && (
                <p>No installments qualify for a new penalty.</p>
              )}
              <footer>
                <button className="payment-button" onClick={close}>
                  Close
                </button>
                <button
                  className="payment-button primary"
                  disabled={!preview.penalized}
                  onClick={applyPenalties}
                >
                  Review & apply
                </button>
              </footer>
            </>
          )}
          {dialog === "adjustment" && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (selected)
                  save(
                    () =>
                      api.payments.ledger.create({
                        ...adjust,
                        clientId: selected.clientId,
                        saleId: selected.id,
                        type: "ADJUSTMENT",
                        amount: Number(adjust.amount),
                      }),
                    "Journal adjustment recorded",
                  );
              }}
            >
              <p>
                A journal-only correction. Positive amounts add a credit;
                negative amounts add a debit. This does not change payment
                schedules or sale balances.
              </p>
              {salePicker}
              <fieldset disabled={busy || formLoading}>
                <label>
                  Signed amount (₱)
                  <input
                    required
                    type="number"
                    step="0.01"
                    value={adjust.amount}
                    onChange={(e) =>
                      setAdjust({ ...adjust, amount: e.target.value })
                    }
                  />
                </label>
                <label>
                  Reason for adjustment
                  <textarea
                    required
                    maxLength={1000}
                    rows={3}
                    value={adjust.description}
                    onChange={(e) =>
                      setAdjust({ ...adjust, description: e.target.value })
                    }
                  />
                </label>
                <label>
                  Reference
                  <input
                    maxLength={100}
                    value={adjust.referenceNumber}
                    onChange={(e) =>
                      setAdjust({ ...adjust, referenceNumber: e.target.value })
                    }
                  />
                </label>
              </fieldset>
              <footer>
                <button
                  type="button"
                  className="payment-button"
                  disabled={busy}
                  onClick={close}
                >
                  Cancel
                </button>
                <button
                  className="payment-button primary"
                  disabled={
                    busy || formLoading || !selected || !Number(adjust.amount)
                  }
                >
                  {busy ? "Saving…" : "Record adjustment"}
                </button>
              </footer>
            </form>
          )}
          {dialog === "schedule" && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!selected) return;
                confirmAction({
                  title: selected.payments.length
                    ? "Replace this schedule?"
                    : "Create this schedule?",
                  description: `Set ${schedule.frequency} installments for ${name(selected.client)}, Lot ${selected.lot.lotNumber}, starting ${date(schedule.startDate + "T00:00:00+08:00")}. Existing unpaid dates will be replaced.`,
                  confirmLabel: "Save schedule",
                  pendingLabel: "Saving…",
                  successTitle: "Schedule saved",
                  action: async () => {
                    await api.payments.schedules.generate({
                      saleId: selected.id,
                      expectedUpdatedAt: selected.updatedAt,
                      ...schedule,
                    });
                    setDialog(null);
                    setRefresh((n) => n + 1);
                  },
                });
              }}
            >
              <p>
                Distribute the contract balance after the down payment over the
                sale’s installment term. Month-end dates are clamped to the last
                valid day.
              </p>
              {salePicker}
              <div className="payment-form-grid">
                <label>
                  Frequency
                  <select
                    value={schedule.frequency}
                    onChange={(e) =>
                      setSchedule({ ...schedule, frequency: e.target.value })
                    }
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                </label>
                <label>
                  First due date
                  <input
                    required
                    type="date"
                    value={schedule.startDate}
                    onChange={(e) =>
                      setSchedule({ ...schedule, startDate: e.target.value })
                    }
                  />
                </label>
              </div>
              {selected?.payments.some(
                (p) => p.amountPaid > 0 || p.penaltyAmount > 0,
              ) && (
                <p className="payment-danger">
                  This schedule has receipts or penalties and cannot be
                  replaced.
                </p>
              )}
              <footer>
                <button
                  type="button"
                  className="payment-button"
                  onClick={close}
                >
                  Cancel
                </button>
                <button
                  className="payment-button primary"
                  disabled={
                    formLoading ||
                    !selected ||
                    !!selected?.paymentIssues?.length ||
                    selected?.payments.some(
                      (p) => p.amountPaid > 0 || p.penaltyAmount > 0,
                    )
                  }
                >
                  Review schedule
                </button>
              </footer>
            </form>
          )}
        </div>
      </SalesDialog>
    </>
  );
}
